// Cross-tab notification used by the magic-link auth flow.
//
// When a user clicks the magic link in their email, that link typically opens
// in a new tab. The login screen they came from is still open in another tab
// waiting on a 6-character code. We use BroadcastChannel (with a localStorage
// fallback for browsers without it) to tell the original tab "you are now
// signed in" so it can progress without the user manually finishing the code
// entry.

const CHANNEL_NAME = 'patient-partner-auth';
const STORAGE_KEY = 'patient_partner_auth_event';

const getChannel = () => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  try {
    return new BroadcastChannel(CHANNEL_NAME);
  } catch (e) {
    return null;
  }
};

export const broadcastAuthEvent = (type, payload = {}) => {
  if (typeof window === 'undefined') return;

  const message = { type: type, payload: payload, at: Date.now() };

  const channel = getChannel();
  if (channel) {
    try {
      channel.postMessage(message);
    } catch (e) {
      // ignore
    } finally {
      channel.close();
    }
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
    // Remove immediately so the next event with the same shape still fires the
    // 'storage' event for listeners.
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore (private mode, etc)
  }
};

export const subscribeToAuthEvents = (handler) => {
  if (typeof window === 'undefined') return () => {};

  const channel = getChannel();
  const channelListener = (event) => {
    if (event && event.data && event.data.type) {
      handler(event.data);
    }
  };

  const storageListener = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      const parsed = JSON.parse(event.newValue);
      if (parsed && parsed.type) handler(parsed);
    } catch (e) {
      // ignore
    }
  };

  if (channel) channel.addEventListener('message', channelListener);
  window.addEventListener('storage', storageListener);

  return () => {
    if (channel) {
      try {
        channel.removeEventListener('message', channelListener);
      } finally {
        channel.close();
      }
    }
    window.removeEventListener('storage', storageListener);
  };
};

export const AUTH_EVENT_SIGNED_IN = 'signed_in';
export const AUTH_EVENT_SIGNED_IN_ACK = 'signed_in_ack';
